import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

const formatCurrency = (val) => {
  const num = Number(val || 0);
  return `Rs ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-LK');
};

/**
 * Displays post-maturity (late) interest rules, balance, and cycle breakdown.
 * Pass `breakdown` from API: postMaturityInterestBreakdown or nested in payable breakdown.
 */
const PostMaturityInterestPanel = ({ breakdown, title = 'Post-Maturity Interest (Late Payment)' }) => {
  if (!breakdown) {
    return null;
  }

  const interestDue = Number(breakdown.postMaturityInterestBalance || 0);
  const interestApplied = breakdown.interestApplied === true || interestDue > 0;

  if (!interestApplied && breakdown.beforeLoanEndDate) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Loan end date: {formatDate(breakdown.loanEndDate)}. No late interest is charged before the day after this date.
      </Alert>
    );
  }

  if (!interestApplied && !breakdown.beforeLoanEndDate) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No post-maturity interest is currently due on this loan.
      </Alert>
    );
  }

  const steps = breakdown.calculationSteps || [];
  const cycles = breakdown.appliedCycles || [];

  return (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.dark' }}>
            {title}
          </Typography>
          <Chip label={formatCurrency(interestDue)} color="warning" sx={{ fontWeight: 700 }} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Typography variant="body2"><strong>Loan end date:</strong> {formatDate(breakdown.loanEndDate)}</Typography>
          <Typography variant="body2"><strong>First interest date:</strong> {formatDate(breakdown.firstInterestDate)}</Typography>
          <Typography variant="body2"><strong>Rate per cycle:</strong> {Number(breakdown.interestRate || 0).toFixed(2)}%</Typography>
          <Typography variant="body2"><strong>Cycle every:</strong> {breakdown.interestCycleDays || 10} days</Typography>
          <Typography variant="body2"><strong>Outstanding principal:</strong> {formatCurrency(breakdown.principalOutstanding)}</Typography>
          <Typography variant="body2"><strong>Last cycle applied:</strong> {formatDate(breakdown.lastPostMaturityInterestAppliedDate)}</Typography>
          <Typography variant="body2"><strong>Next cycle (if unpaid):</strong> {formatDate(breakdown.nextInterestCycleDate)}</Typography>
          <Typography variant="body2"><strong>Total due (principal + interest):</strong> {formatCurrency(breakdown.totalOutstandingWithInterest)}</Typography>
        </Box>

        {steps.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              How this was calculated (explain to customer)
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
              {steps.map((step, idx) => (
                <Typography component="li" variant="body2" key={idx} sx={{ mb: 0.5 }}>
                  {step}
                </Typography>
              ))}
            </Box>
          </>
        )}

        {cycles.length > 0 && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Applied interest cycles ({breakdown.appliedCycleCount || cycles.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Cycle date</strong></TableCell>
                  <TableCell align="right"><strong>Balance before</strong></TableCell>
                  <TableCell align="right"><strong>Interest added</strong></TableCell>
                  <TableCell align="right"><strong>Balance after</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cycles.map((cycle, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{formatDate(cycle.cycleDate)}</TableCell>
                    <TableCell align="right">{formatCurrency(cycle.balanceBeforeInterest)}</TableCell>
                    <TableCell align="right">{formatCurrency(cycle.interestAdded)}</TableCell>
                    <TableCell align="right">{formatCurrency(cycle.balanceAfterInterest)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PostMaturityInterestPanel;
